/**
 * All UI copy, in both languages.
 *
 * Quiet, trade-literate, second person. `en` is typed against `fa`, so a
 * forgotten key is a build error rather than a blank label on someone's phone.
 * Authenticity is evidence, never a verdict — in either script.
 */
import { getLang, useLang, type Lang } from "./lang";
import type { PriceKey } from "../api/types";

const faPriceItems: Record<PriceKey, string> = {
  gold_18: "طلای 18 عیار",
  gold_24: "طلای 24 عیار",
  gold_mesghal: "مثقال طلا",
  coin_emami: "سکه امامی",
  coin_bahar: "سکه بهار آزادی",
  coin_half: "نیم سکه",
  coin_quarter: "ربع سکه",
  coin_gram: "سکه گرمی",
  silver_999: "نقره 999",
  silver_925: "نقره 925",
  gold_ounce: "انس طلا",
  silver_ounce: "انس نقره",
  usd: "دلار آمریکا",
  eur: "یورو",
  aed: "درهم امارات",
};

const enPriceItems: Record<PriceKey, string> = {
  gold_18: "18k gold",
  gold_24: "24k gold",
  gold_mesghal: "Gold mesghal",
  coin_emami: "Emami coin",
  coin_bahar: "Bahar Azadi coin",
  coin_half: "Half coin",
  coin_quarter: "Quarter coin",
  coin_gram: "Gram coin",
  silver_999: "Silver 999",
  silver_925: "Sterling 925",
  gold_ounce: "Gold ounce",
  silver_ounce: "Silver ounce",
  usd: "US dollar",
  eur: "Euro",
  aed: "UAE dirham",
};

const fa = {
  appName: "زردیر",

  tabs: {
    pieces: "قطعات",
    prices: "قیمت روز",
    add: "افزودن",
  },

  actions: {
    save: "ذخیره",
    saving: "در حال ذخیره…",
    saved: "ذخیره شد",
    cancel: "انصراف",
    edit: "ویرایش",
    delete: "حذف",
    confirmDelete: "حذف قطعه",
    undo: "بازگردانی",
    clearAll: "پاک کردن همه",
    apply: "اعمال",
    addOption: "افزودن گزینه",
    add: "افزودن",
    retry: "تلاش دوباره",
    refresh: "به‌روزرسانی",
    close: "بستن",
    search: "جستجو",
    filter: "فیلتر",
    select: "انتخاب",
    remove: "حذف",
    makePrimary: "تصویر اصلی شود",
    choose: "انتخاب فایل",
    open: "باز کردن",
    back: "بازگشت",
    done: "تمام",
    viewGrid: "نمایش شبکه‌ای",
    viewList: "نمایش فهرستی",
    language: "زبان",
  },

  home: {
    title: "قطعات",
    searchPlaceholder: "جستجو در عنوان، کد، سازنده، انگ",
    emptyTitle: "هنوز قطعه‌ای نیست",
    emptyBody: "اولین قطعه را ثبت کنید.",
    noHitsTitle: "نتیجه‌ای نیست",
    noHitsBody: "جستجو یا فیلترها را تغییر دهید.",
    countSuffix: (_n: number) => "قطعه",
  },

  filters: {
    title: "فیلترها",
    any: "همه",
    anyOf: "هر کدام از موارد",
    purityPlaceholder: "مثلاً 750",
    purityChip: (permille: number) => `عیار ${permille}`,
    withStones: "با سنگ",
    withoutStones: "بدون سنگ",
    removeOne: (label: string) => `حذف فیلتر ${label}`,
  },

  form: {
    newTitle: "قطعهٔ تازه",
    editTitle: "ویرایش قطعه",
    sections: {
      identity: "شناسایی",
      metal: "فلز و وزن",
      stones: "سنگ‌ها",
      make: "ساخت",
      ownership: "موجودی و قیمت",
      condition: "وضعیت",
      provenance: "منشأ",
      authenticity: "مدارک",
      notes: "یادداشت",
      media: "تصاویر",
    },
    titlePlaceholder: "مثلاً انگشتر سولیتر",
    inventoryPlaceholder: "اختیاری",
    makerPlaceholder: "کارگاه یا برند",
    hallmarksPlaceholder: "مهر، انگ، کد عیار",
    sizePlaceholder: "سایز انگشتر، طول زنجیر…",
    gram: "گرم",
    percent: "درصد",
    purityHint: "به هزارم: 750 یعنی 18 عیار، 925 یعنی نقرهٔ استرلینگ.",
    grossHint: "کل قطعه، با سنگ.",
    netHint: "فقط فلز. ارزش روز از همین محاسبه می‌شود.",
    makingHint: "درصد اجرت روی ارزش فلز.",
    yearPrecision: {
      exact: "دقیق",
      circa: "حدوداً",
      range: "بازه",
      unknown: "نامشخص",
    },
    yearFrom: "از سال",
    yearTo: "تا سال",
    addStone: "افزودن ردیف سنگ",
    stoneLine: (n: number) => `ردیف ${n}`,
    stonesEmpty: "سنگی ثبت نشده.",
    colorPlaceholder: "D–Z یا توصیف رنگ",
    certificatePlaceholder: "شمارهٔ گزارش آزمایشگاه",
    caratTotal: "قیراط (جمع ردیف)",
    optional: "اختیاری",
    none: "—",
    newOptionPlaceholder: "عنوان گزینهٔ تازه",
    dirtyDiscard: "تغییرات ذخیره‌نشده رها شود؟",
    requiredTitle: "عنوان قطعه را وارد کنید.",
    requiredKind: "نوع قطعه را انتخاب کنید.",
    requiredStoneType: "نوع سنگ را انتخاب کنید.",
    countMin: "تعداد باید عددی صحیح و دست‌کم 1 باشد.",
    purityInteger: "عیار عدد صحیح است.",
    weightPositive: "وزن باید بزرگ‌تر از صفر باشد.",
    netAboveGross: "وزن فلز نمی‌تواند از وزن کل بیشتر باشد.",
    purityRange: "عیار به هزارم است، از 1 تا 1000.",
    caratPositive: "قیراط باید بزرگ‌تر از صفر باشد.",
    makingNegative: "اجرت نمی‌تواند منفی باشد.",
    stonePrice: "قیمت هر عدد",
    stoneLineToman: (amount: string) => `≈ ${amount} تومان برای این ردیف`,
    stoneNoRate: "نرخ روز این ارز در دسترس نیست.",
    yearStartRequired: "سال را وارد کنید.",
    yearEndRequired: "پایان بازه را وارد کنید.",
    yearOrder: "پایان بازه نمی‌تواند پیش از آغاز آن باشد.",
  },

  detail: {
    untitled: "بدون عنوان",
    noMedia: "بدون تصویر",
    deletePrompt: "این قطعه از فهرست حذف می‌شود. می‌توانید بلافاصله بازگردانید.",
    deleted: "قطعه حذف شد",
    restored: "قطعه بازگردانده شد",
    /** Never a verdict. A document is evidence and nothing more. */
    authenticityNote:
      "این مدارک شواهد ثبت‌شده‌اند، نه تأیید اصالت. داوری نهایی بر عهدهٔ کارشناس است.",
    noFiles: "فایلی پیوست نشده",
    metalValue: "ارزش روز فلز",
    withMaking: (percent: string) => `با ${percent}٪ اجرت`,
    valueBasis: (grams: string, purity: number) => `${grams} گرم × عیار ${purity}`,
    price: "قیمت محاسبه‌شده",
    priceHint:
      "از وزن، عیار، اجرت و قیمت سنگ‌ها با نرخ روز محاسبه می‌شود و دستی وارد نمی‌شود. مالیات بر ارزش افزوده در آن نیست.",
    stonesValue: "سنگ‌ها",
    total: "جمع",
    soldAt: (time: string) => `در لحظهٔ فروش ثابت شد · ${time}`,
    missing: {
      weight: "وزن را وارد کنید.",
      purity: "عیار را وارد کنید.",
      metal_rate: "نرخ روز این فلز در دسترس نیست.",
      stone_price: "برای همهٔ سنگ‌ها قیمت وارد کنید.",
      stone_rate: "نرخ ارز یکی از سنگ‌ها در دسترس نیست.",
      nothing: "برای قیمت‌گذاری، فلز یا سنگ ثبت کنید.",
      board: "نرخ‌های بازار هنوز بارگذاری نشده‌اند.",
    },
    stoneCount: (n: number) => `${n} عدد`,
    carat: (value: string) => `${value} قیراط`,
    gross: "وزن کل",
    net: "وزن فلز",
  },

  prices: {
    title: "قیمت روز",
    toman: "تومان",
    dollar: "دلار",
    perGram: "هر گرم",
    perMesghal: "هر مثقال",
    perCoin: "هر سکه",
    perOunce: "هر انس",
    perUnit: "",
    groups: {
      gold: "طلا",
      coins: "سکه",
      silver: "نقره",
      global: "بازار جهانی",
      currencies: "ارز",
    },
    items: faPriceItems,
    asOf: (time: string) => `به‌روز: ${time}`,
    source: "منبع: tgju.org",
    /** A rate on a phone is a reference, never an offer. */
    disclaimer: "نرخ بازار آزاد و صرفاً جهت اطلاع است؛ قیمت قطعی خرید و فروش نیست.",
    stale: "به‌روزرسانی نرخ‌ها ناموفق بود؛ آخرین نرخ ذخیره‌شده نمایش داده می‌شود.",
    unavailable: "نرخ بازار الان در دسترس نیست.",
  },

  media: {
    primary: "تصویر اصلی",
    conditionFiles: "مستندات وضعیت",
    authenticityFiles: "مدارک / گواهی",
    pickImageOrVideo: "افزودن تصویر یا ویدیو",
    pickFile: "افزودن فایل",
    uploading: "در حال بارگذاری…",
    processing: "در حال آماده‌سازی…",
    uploadFailed: "بارگذاری ناموفق بود",
    captionPlaceholder: "توضیح (اختیاری)",
    tooLarge: (limitMb: number) => `حجم فایل بیش از ${limitMb} مگابایت است.`,
    tooMany: (max: number) => `برای هر قطعه حداکثر ${max} تصویر یا ویدیو.`,
    quotaExceeded: "فضای ذخیره‌سازی شما پر شده است.",
    wrongKind: "این نوع فایل برای این بخش مجاز نیست.",
    afterSave: "پس از ذخیرهٔ قطعه بارگذاری می‌شود.",
  },

  access: {
    title: "دسترسی به اپ",
    hint: "فقط با پیوند دعوت. هر نفر کاتالوگ خودش را می‌گیرد.",
    noMembers: "هنوز کسی در فهرست دسترسی نیست.",
    anonymousMember: "بدون نام",
    roleAdmin: "ادمین",
    roleUser: "کاربر",
    memberRemoved: "دسترسی به اپ برداشته شد",
    removeTitle: "حذف دسترسی به اپ؟",
    removeBody:
      "دیگر نمی‌توانند مینی‌اپ را باز کنند. کاتالوگ و قطعاتشان پاک نمی‌شود؛ فقط مجوز ورود برداشته می‌شود.",
    removeConfirm: "حذف دسترسی",
    admitTitle: "دعوت کاربر",
    admitHint: "ورود به اپ با کاتالوگ شخصی. مدیریت دسترسی ندارد.",
    admitInviteCreate: "ساخت پیوند کاربر",
    admitInviteCreated: "پیوند کاربر ساخته شد",
    admitLinkLabel: "پیوند کاربر",
    adminTitle: "دعوت ادمین",
    adminHint: "ورود به اپ با کاتالوگ شخصی، به‌علاوه دعوت کاربر (و ادمین).",
    adminInviteCreate: "ساخت پیوند ادمین",
    adminInviteCreated: "پیوند ادمین ساخته شد",
    adminLinkLabel: "پیوند ادمین",
    admitShareText: "دعوت کاربر به Zardir",
    adminShareText: "دعوت ادمین Zardir",
  },

  /** The owner's controls for the customer page behind a QR tag. */
  share: {
    title: "صفحهٔ مشتری",
    toggle: "نمایش این قطعه با کد QR",
    intro:
      "مشتری با اسکن کد، عنوان، عکس‌ها، فلز، عیار، وزن، سنگ‌ها، سازنده، وضعیت فروش و قیمت را می‌بیند. یادداشت‌ها، مبدأ، مدارک و کد انبار خصوصی می‌مانند.",
    offNote: "خاموش است؛ کسی که کد را اسکن کند چیزی نمی‌بیند.",
    printHint: "این کد را روی برچسب قطعه چاپ کنید.",
    copy: "کپی",
    copied: "پیوند کپی شد",
    copyFailed: "کپی نشد؛ پیوند را نگه دارید و دستی کپی کنید.",
    send: "ارسال",
    open: "باز کردن",
    rotate: "کد جدید",
    rotateTitle: "ساخت کد جدید",
    rotatePrompt: "کد فعلی باطل می‌شود و برچسب‌هایی که با آن چاپ شده‌اند دیگر کار نمی‌کنند.",
    rotated: "کد جدید ساخته شد",
    published: "صفحهٔ مشتری روشن شد",
    unpublished: "صفحهٔ مشتری خاموش شد",
    qrLabel: "کد QR صفحهٔ مشتری",
    download: "دریافت فایل QR",
    downloading: "در حال ارسال…",
    downloadHint: "فایل چاپی QR در گفتگو با ربات فرستاده می‌شود.",
    sent: "فایل QR در گفتگو با ربات فرستاده شد",
    startBot: "اول گفتگو با ربات را باز کنید و Start را بزنید، بعد دوباره امتحان کنید.",
    sendFailed: "ارسال نشد؛ اتصال به تلگرام برقرار نشد. دوباره امتحان کنید.",
  },

  /** The customer page itself (zarinfo). No login, no owner copy. */
  publicPage: {
    availability: {
      available: "موجود",
      reserved: "رزرو شده",
      sold: "فروخته شد",
    },
    price: "قیمت",
    estimate: "قیمت تخمینی امروز",
    vatNote: "مالیات بر ارزش افزوده هنگام خرید به این مبلغ افزوده می‌شود.",
    specs: "مشخصات",
    notFound: "این قطعه در دسترس نیست",
    notFoundBody: "پیوند باطل شده یا فروشنده نمایش آن را خاموش کرده است.",
    retry: "تلاش دوباره",
    gallery: (n: number, total: number) => `تصویر ${n} از ${total}`,
    viewPhoto: "نمایش تمام‌صفحهٔ تصویر",
    zoomIn: "بزرگ‌نمایی",
    zoomOut: "اندازهٔ اصلی",
    previous: "تصویر قبلی",
    next: "تصویر بعدی",
  },

  errors: {
    title: "مشکلی پیش آمد",
    network: "ارتباط با سرور برقرار نشد.",
    notInTelegram: "این صفحه را از داخل تلگرام باز کنید.",
    unauthorized: "هویت شما تأیید نشد. اپ را از داخل تلگرام دوباره باز کنید.",
    forbidden: "این حساب تلگرام اجازهٔ دسترسی ندارد.",
    notFound: "این قطعه پیدا نشد.",
    throttled: (seconds: number) => `درخواست‌ها زیاد بود. ${seconds} ثانیه صبر کنید.`,
    referenced: "این مورد هنوز استفاده شده و حذف نمی‌شود.",
    server: "خطای سرور.",
    amountNegative: "مبلغ نمی‌تواند منفی باشد.",
    unknownYearWithValue: "برای سال نامشخص نباید عددی وارد شود.",
    duplicate: "این کد از قبل برای قطعهٔ دیگری ثبت شده.",
  },

  loading: "در حال بارگذاری…",
};

/** `en` is typed against this, so a forgotten key fails the build. */
export type Strings = typeof fa;

const en: Strings = {
  appName: "Zardir",

  tabs: {
    pieces: "Pieces",
    prices: "Prices",
    add: "Add",
  },

  actions: {
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    cancel: "Cancel",
    edit: "Edit",
    delete: "Delete",
    confirmDelete: "Delete piece",
    undo: "Undo",
    clearAll: "Clear all",
    apply: "Apply",
    addOption: "Add option",
    add: "Add",
    retry: "Try again",
    refresh: "Refresh",
    close: "Close",
    search: "Search",
    filter: "Filter",
    select: "Select",
    remove: "Remove",
    makePrimary: "Make primary image",
    choose: "Choose file",
    open: "Open",
    back: "Back",
    done: "Done",
    viewGrid: "Grid view",
    viewList: "List view",
    language: "Language",
  },

  home: {
    title: "Pieces",
    searchPlaceholder: "Search title, code, maker, hallmark",
    emptyTitle: "No pieces yet",
    emptyBody: "Record your first piece.",
    noHitsTitle: "No results",
    noHitsBody: "Change the search or the filters.",
    countSuffix: (n: number) => (n === 1 ? "piece" : "pieces"),
  },

  filters: {
    title: "Filters",
    any: "Any",
    anyOf: "Any of",
    purityPlaceholder: "e.g. 750",
    purityChip: (permille: number) => `Purity ${permille}`,
    withStones: "With stones",
    withoutStones: "No stones",
    removeOne: (label: string) => `Remove filter ${label}`,
  },

  form: {
    newTitle: "New piece",
    editTitle: "Edit piece",
    sections: {
      identity: "Identity",
      metal: "Metal and weight",
      stones: "Stones",
      make: "Make",
      ownership: "Stock and price",
      condition: "Condition",
      provenance: "Provenance",
      authenticity: "Evidence",
      notes: "Notes",
      media: "Images",
    },
    titlePlaceholder: "e.g. Solitaire ring",
    inventoryPlaceholder: "Optional",
    makerPlaceholder: "Workshop or brand",
    hallmarksPlaceholder: "Stamp, hallmark, purity mark",
    sizePlaceholder: "Ring size, chain length…",
    gram: "g",
    percent: "%",
    purityHint: "Parts per thousand: 750 is 18k, 925 is sterling silver.",
    grossHint: "Whole piece, stones included.",
    netHint: "Metal only. The day's value is worked out from this.",
    makingHint: "Making charge as a percent of metal value.",
    yearPrecision: {
      exact: "Exact",
      circa: "Circa",
      range: "Range",
      unknown: "Unknown",
    },
    yearFrom: "From",
    yearTo: "To",
    addStone: "Add a stone line",
    stoneLine: (n: number) => `Line ${n}`,
    stonesEmpty: "No stones recorded.",
    colorPlaceholder: "D–Z or a description",
    certificatePlaceholder: "Lab report number",
    caratTotal: "Carat (line total)",
    optional: "Optional",
    none: "—",
    newOptionPlaceholder: "Name of the new option",
    dirtyDiscard: "Discard unsaved changes?",
    requiredTitle: "Enter the title of the piece.",
    requiredKind: "Choose what kind of piece this is.",
    requiredStoneType: "Choose the stone type.",
    countMin: "Count must be a whole number, at least 1.",
    purityInteger: "Purity is a whole number.",
    weightPositive: "Weight must be greater than zero.",
    netAboveGross: "Metal weight cannot exceed the total weight.",
    purityRange: "Purity is parts per thousand, 1 to 1000.",
    caratPositive: "Carat must be greater than zero.",
    makingNegative: "The making charge cannot be negative.",
    stonePrice: "Price per stone",
    stoneLineToman: (amount: string) => `≈ ${amount} toman for this line`,
    stoneNoRate: "No market rate for this currency right now.",
    yearStartRequired: "Enter the year.",
    yearEndRequired: "Enter the end of the range.",
    yearOrder: "The end of a range cannot come before its start.",
  },

  detail: {
    untitled: "Untitled",
    noMedia: "No image",
    deletePrompt: "This piece leaves the list. You can restore it right away.",
    deleted: "Piece deleted",
    restored: "Piece restored",
    authenticityNote:
      "These are recorded documents, not a confirmation of authenticity. The final judgement rests with an expert.",
    noFiles: "No files attached",
    metalValue: "Metal value today",
    withMaking: (percent: string) => `With ${percent}% making charge`,
    valueBasis: (grams: string, purity: number) => `${grams} g × purity ${purity}`,
    price: "Worked-out price",
    priceHint:
      "Worked out from weight, purity, making charge and stone prices at today's rates; never typed in. VAT is not included.",
    stonesValue: "Stones",
    total: "Total",
    soldAt: (time: string) => `Fixed at the moment of sale · ${time}`,
    missing: {
      weight: "Enter the weight.",
      purity: "Enter the purity.",
      metal_rate: "No market rate for this metal right now.",
      stone_price: "Enter a price for every stone.",
      stone_rate: "No market rate for one of the stone currencies.",
      nothing: "Add a metal or stones to price this piece.",
      board: "Market rates have not loaded yet.",
    },
    stoneCount: (n: number) => `× ${n}`,
    carat: (value: string) => `${value} ct`,
    gross: "Total weight",
    net: "Metal weight",
  },

  prices: {
    title: "Market prices",
    toman: "toman",
    dollar: "USD",
    perGram: "per gram",
    perMesghal: "per mesghal",
    perCoin: "per coin",
    perOunce: "per ounce",
    perUnit: "",
    groups: {
      gold: "Gold",
      coins: "Coins",
      silver: "Silver",
      global: "Global market",
      currencies: "Currencies",
    },
    items: enPriceItems,
    asOf: (time: string) => `As of ${time}`,
    source: "Source: tgju.org",
    disclaimer: "Open-market rate for reference only; not a buy or sell quote.",
    stale: "Could not refresh the rates; showing the last saved board.",
    unavailable: "Market prices are unavailable right now.",
  },

  media: {
    primary: "Primary image",
    conditionFiles: "Condition documents",
    authenticityFiles: "Evidence / certificates",
    pickImageOrVideo: "Add an image or video",
    pickFile: "Add a file",
    uploading: "Uploading…",
    processing: "Preparing…",
    uploadFailed: "Upload failed",
    captionPlaceholder: "Caption (optional)",
    tooLarge: (limitMb: number) => `This file is larger than ${limitMb} MB.`,
    tooMany: (max: number) => `A piece holds at most ${max} images or videos.`,
    quotaExceeded: "Your storage is full.",
    wrongKind: "This kind of file is not allowed here.",
    afterSave: "Uploads once the piece is saved.",
  },

  access: {
    title: "App access",
    hint: "Invite with links only. Each person gets their own catalog.",
    noMembers: "No one on the access list yet.",
    anonymousMember: "Unnamed",
    roleAdmin: "Admin",
    roleUser: "User",
    memberRemoved: "App access removed",
    removeTitle: "Remove app access?",
    removeBody:
      "They will no longer be able to open the Mini App. Their catalog and pieces are kept; only admission is revoked.",
    removeConfirm: "Remove access",
    admitTitle: "Invite a user",
    admitHint: "Opens the app with their own catalog. Cannot manage access.",
    admitInviteCreate: "Create user link",
    admitInviteCreated: "User link created",
    admitLinkLabel: "User invite link",
    adminTitle: "Invite an admin",
    adminHint: "Opens the app with their own catalog, plus can invite users (and admins).",
    adminInviteCreate: "Create admin link",
    adminInviteCreated: "Admin link created",
    adminLinkLabel: "Admin invite link",
    admitShareText: "User invitation to Zardir",
    adminShareText: "Admin invitation to Zardir",
  },

  share: {
    title: "Customer page",
    toggle: "Show this piece by QR code",
    intro:
      "A customer who scans the code sees the title, photos, metal, purity, weight, stones, maker, availability and price. Notes, provenance, evidence and the inventory code stay private.",
    offNote: "Off. Anyone who scans the code sees nothing.",
    printHint: "Print this code on the piece's tag.",
    copy: "Copy",
    copied: "Link copied",
    copyFailed: "Could not copy; press and hold the link to copy it.",
    send: "Send",
    open: "Open",
    rotate: "New code",
    rotateTitle: "Make a new code",
    rotatePrompt: "The current code stops working, along with every tag printed with it.",
    rotated: "New code made",
    published: "Customer page is on",
    unpublished: "Customer page is off",
    qrLabel: "Customer page QR code",
    download: "Get QR file",
    downloading: "Sending…",
    downloadHint: "A print-ready QR file arrives in your chat with the bot.",
    sent: "QR file sent to your chat with the bot",
    startBot: "Open your chat with the bot and press Start, then try again.",
    sendFailed: "Could not reach Telegram. Try again.",
  },

  publicPage: {
    availability: {
      available: "Available",
      reserved: "Reserved",
      sold: "Sold",
    },
    price: "Price",
    estimate: "Today's estimated price",
    vatNote: "VAT is added to this amount at purchase.",
    specs: "Details",
    notFound: "This piece is not available",
    notFoundBody: "The link was revoked, or the seller has turned its page off.",
    retry: "Try again",
    gallery: (n: number, total: number) => `Image ${n} of ${total}`,
    viewPhoto: "View photo full screen",
    zoomIn: "Zoom in",
    zoomOut: "Fit to screen",
    previous: "Previous image",
    next: "Next image",
  },

  errors: {
    title: "Something went wrong",
    network: "Could not reach the server.",
    notInTelegram: "Open this page from inside Telegram.",
    unauthorized: "You could not be verified. Reopen the app from inside Telegram.",
    forbidden: "This Telegram account does not have access.",
    notFound: "This piece could not be found.",
    throttled: (seconds: number) => `Too many requests. Wait ${seconds} seconds.`,
    referenced: "This is still in use, so it cannot be removed.",
    server: "Server error.",
    amountNegative: "An amount cannot be negative.",
    unknownYearWithValue: "An unknown year cannot carry a number.",
    duplicate: "Another piece already uses this code.",
  },

  loading: "Loading…",
};

export const strings: Record<Lang, Strings> = { fa, en };

/** For components. Re-renders the caller when the language changes. */
export function useT(): Strings {
  return strings[useLang()];
}

/** For the handful of callers that are not components (api/*, form helpers). */
export function tr(lang: Lang = getLang()): Strings {
  return strings[lang];
}

/**
 * DRF speaks English. These are the messages the server can realistically send
 * to this UI; anything unmapped falls through as-is rather than being hidden.
 */
const serverMessages: Array<[RegExp, (s: Strings) => string]> = [
  [/^Title cannot be blank\.$/, (s) => s.form.requiredTitle],
  [/^Weight must be greater than 0\.$/, (s) => s.form.weightPositive],
  [/^Metal weight cannot exceed/, (s) => s.form.netAboveGross],
  [/^Purity is parts per thousand/, (s) => s.form.purityRange],
  [/^Carat must be greater than 0\.$/, (s) => s.form.caratPositive],
  [/^Making charge cannot be negative\.$/, (s) => s.form.makingNegative],
  [/^Amounts cannot be negative\.$/, (s) => s.errors.amountNegative],
  [/^An unknown year cannot carry/, (s) => s.errors.unknownYearWithValue],
  [/^year_start is required/, (s) => s.form.yearStartRequired],
  [/^year_end is required for a year range\.$/, (s) => s.form.yearEndRequired],
  [/^year_end cannot be before year_start\.$/, (s) => s.form.yearOrder],
  [/^Storage quota exceeded\.$/, (s) => s.media.quotaExceeded],
  [/^Market prices are unavailable/, (s) => s.prices.unavailable],
  [/already exists|must make a unique set|unique/i, (s) => s.errors.duplicate],
];

export function localizeServerMessage(message: string, lang: Lang = getLang()): string {
  const s = strings[lang];
  for (const [pattern, select] of serverMessages) {
    if (pattern.test(message)) return select(s);
  }
  return message;
}
